import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/lib/api/types";
import { authApi } from "@/lib/api/auth";
import { CHAT_PREFS_KEY } from "./chat-store";

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  ssoLogin: (code: string, codeVerifier: string) => Promise<void>;
  logout: () => void;
  setTokens: (accessToken: string | undefined, refreshToken: string | undefined) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email, password) => {
        const response = await authApi.login(email, password);
        set({
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
        });
      },

      ssoLogin: async (code, codeVerifier) => {
        const response = await authApi.ssoCallback(code, codeVerifier);
        set({
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          isAuthenticated: true,
        });
      },

      logout: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear chat preferences on logout
        try {
          sessionStorage.removeItem(CHAT_PREFS_KEY);
        } catch {
          // sessionStorage may be unavailable
        }
      },

      setTokens: (accessToken, refreshToken) => {
        const current = get();
        set({
          accessToken: accessToken || current.accessToken,
          refreshToken: refreshToken || current.refreshToken,
        });
      },

      checkAuth: async () => {
        const { accessToken } = get();
        if (!accessToken) {
          set({ isLoading: false });
          return;
        }
        try {
          const user = await authApi.me();
          set({ user, isAuthenticated: true, isLoading: false });
        } catch {
          get().logout();
          set({ isLoading: false });
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    }
  )
);
